import React from 'react';
import { Box, Typography, Grid, Paper } from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import DevicesIcon from '@mui/icons-material/Devices';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import ShieldIcon from '@mui/icons-material/Shield';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import BarChartIcon from '@mui/icons-material/BarChart';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import { useTranslation } from 'react-i18next';

const HEADING_FONT = "'Manrope', sans-serif";
const BODY_FONT = "'Inter', sans-serif";

const cards = [
  {
    icon: VerifiedUserIcon,
    titleKey: 'advantages.iso.title',
    descKey: 'advantages.iso.desc',
    titleFallback: 'ISO 27001 Certified',
    descFallback: 'Data protected by international security standards',
  },
  {
    icon: DevicesIcon,
    titleKey: 'advantages.apps.title',
    descKey: 'advantages.apps.desc',
    titleFallback: 'Cross-platform Apps',
    descFallback: 'Native apps for iOS, Android, Windows, macOS & Web',
  },
  {
    icon: IntegrationInstructionsIcon,
    titleKey: 'advantages.crm.title',
    descKey: 'advantages.crm.desc',
    titleFallback: '50+ CRM Integrations',
    descFallback: 'Seamless integration with Salesforce, HubSpot, Pipedrive & more',
  },
  {
    icon: ShieldIcon,
    titleKey: 'advantages.missed.title',
    descKey: 'advantages.missed.desc',
    titleFallback: 'Missed Call Prevention',
    descFallback: 'Smart call routing, queue callbacks & real-time alerts',
  },
  {
    icon: SupportAgentIcon,
    titleKey: 'advantages.support.title',
    descKey: 'advantages.support.desc',
    titleFallback: 'Support & personal manager',
    descFallback: '95% of clients rate our support as "Good" or "Excellent". Average response time — 2 minutes',
  },
  {
    icon: CloudDoneIcon,
    titleKey: 'advantages.recordings.title',
    descKey: 'advantages.recordings.desc',
    titleFallback: 'Unlimited call recordings',
    descFallback: 'Free unlimited storage of call recordings on all plans',
  },
  {
    icon: BarChartIcon,
    titleKey: 'advantages.analytics.title',
    descKey: 'advantages.analytics.desc',
    titleFallback: 'Powerful analytics & reporting',
    descFallback: 'Real-time dashboards, custom reports & AI-powered insights for every call',
  },
  {
    icon: GraphicEqIcon,
    titleKey: 'advantages.ukraine.title',
    descKey: 'advantages.ukraine.desc',
    titleFallback: 'Made in Ukraine',
    descFallback: 'Ukrainian product built by a team that understands local business needs',
  },
];

export default function AdvantagesSection() {
  const { t } = useTranslation();

  return (
    <Box>
      {/* Section title */}
      <Typography
        sx={{
          fontFamily: HEADING_FONT,
          fontWeight: 700,
          fontSize: { xs: '1.75rem', md: '1.875rem' },
          mb: 5,
        }}
      >
        {t('offer.advantages', 'Why Ringostat?')}
      </Typography>

      <Grid container spacing={3}>
        {cards.map(({ icon: Icon, titleKey, descKey, titleFallback, descFallback }) => (
          <Grid item xs={12} sm={6} md={3} key={titleKey}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 4 },
                borderRadius: 2,
                bgcolor: 'white',
                border: '1px solid rgba(187,203,184,0.12)',
                height: '100%',
                minHeight: { sm: 200 },
                display: 'flex',
                flexDirection: { xs: 'row', sm: 'column' },
                alignItems: { xs: 'center', sm: 'flex-start' },
                gap: 2,
              }}
            >
              <Icon sx={{ fontSize: { xs: 28, sm: 40 }, color: '#006e2a', flexShrink: 0 }} />
              <Box>
                <Typography
                  sx={{
                    fontFamily: HEADING_FONT,
                    fontWeight: 700,
                    fontSize: { xs: '0.9375rem', sm: '1.125rem' },
                  }}
                >
                  {t(titleKey, titleFallback)}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: BODY_FONT,
                    color: '#3c4a3c',
                    fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                    lineHeight: 1.6,
                    mt: 0.5,
                  }}
                >
                  {t(descKey, descFallback)}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
